import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import parse from 'date-fns/parse';
import { Formik, useFormikContext } from 'formik';
import type { FormikProps } from 'formik';
import { formatDate } from 'helpers/date';
import { tap } from 'rxjs/operators';
import Modal from 'v2/components/Shared/Modal';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { financialStatementService } from 'v2/services/financialStatement/statement';

import { initialFilter } from './constants';
import type { ExportStatement, Props } from './constants';
import Form from './Form';
import SuccessStep from './SuccessStep';
import { FilterSchema } from './validations';

function ExportContent({ isOpen, showSuccess, setClose }: Props) {
  const { isValid, isSubmitting, submitForm } = useFormikContext<ExportStatement>();

  return (
    <Modal
      open={isOpen}
      size='sm'
      setClose={setClose}
      textTitle='Exportar'
      textReturnButton='Fechar'
      textProceedButton={showSuccess ? 'Sair' : 'Gerar'}
      handleProceedClick={showSuccess ? setClose : submitForm}
      isProceedButtonDisabled={isSubmitting || !isValid}
      hideReturnButton={showSuccess}
    >
      {showSuccess ? <SuccessStep /> : <Form />}
    </Modal>
  );
}

function ExportModal({ isOpen, setClose }: Props) {
  const [showSuccess, setShowSuccess] = useState(false);
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);

  const [submitRequestExport] = useCallbackObservable((userEmail, values, setSubmitting) => {
    const startDate =
      typeof values.date[0] === 'string' ? parse(values.date[0], 'dd/MM/yyyy', new Date()) : values.date[0].toDate();
    const endDate =
      typeof values.date[1] === 'string' ? parse(values.date[1], 'dd/MM/yyyy', new Date()) : values.date[1].toDate();

    const dataRequest = {
      start_date: formatDate(startDate, 'yyyy-MM-dd'),
      end_date: formatDate(endDate, 'yyyy-MM-dd'),
      email: values.email || userEmail,
      export_in_future: values.export_in_future,
      export_blocked: values.export_blocked
    };

    return financialStatementService.requestExport(dataRequest).pipe(
      tap(
        () => {
          toast.success('Sucesso, arquivos de exportação solicitados com êxito!');
          setShowSuccess(true);
          setSubmitting(false);
        },
        err => {
          toast.error(err);
          setSubmitting(false);
        }
      )
    );
  }, []);

  const onSubmit = (values: ExportStatement, { setSubmitting }: FormikProps<ExportStatement>) => {
    submitRequestExport(user?.email, values, setSubmitting);
  };

  return (
    <Formik initialValues={initialFilter} validationSchema={FilterSchema} onSubmit={onSubmit}>
      <ExportContent isOpen={isOpen} setClose={setClose} showSuccess={showSuccess} />
    </Formik>
  );
}

export default ExportModal;
